import { Fab } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { getLang } from '@/locale';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import MonthView, { DAY_NAMES_MAP } from './components/MonthView';
import { DatePickerManagerOutput, DatePickerProps } from './DatePicker.types';
import { findInitialPageIndex, getOverlappingMonthPairs } from './helpers';

function WeekDays() {
  return (
    <>
      {DAY_NAMES_MAP[getLang()].map((name) => (
        <span className="text-l text-fg_secondary" key={name}>
          {name}
        </span>
      ))}
    </>
  );
}

const View: {
  [key in DatePickerProps['type']]: React.FC<DatePickerManagerOutput>;
} = {
  continuous: ContinuousDatePicker,
  paged: PaginatedDatePicker,
};

function ContinuousDatePicker({
  continuousScrollContainer,
  selectedRange,
  visibleMonths,
  onDateSelect,
}: DatePickerProps) {
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  useEffect(() => {
    const scrollContainer = continuousScrollContainer.current;
    if (!scrollContainer) return;

    const handleOnScroll = () => {
      let _scrollPosition = scrollContainer.scrollTop;

      if (_scrollPosition !== scrollPosition) {
        setScrollPosition(_scrollPosition);
      }
    };
    scrollContainer.addEventListener('scroll', handleOnScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleOnScroll);
    };
  }, [continuousScrollContainer]);

  return (
    <div className="mx-auto">
      <div
        className={classnames(
          'bg-surface_l2 -mx-md sticky top-0 transition-shadow duration-300',
          scrollPosition > 48 && 'shadow-xs',
        )}>
        <div className={classnames('px-md grid h-[48px] grid-cols-8 place-content-center text-center text-xs')}>
          {/* Empty cell for week number header */}
          <div></div>
          <WeekDays />
        </div>
      </div>
      <div className="flex flex-col items-center justify-center rounded-lg md:flex-row md:flex-wrap">
        {visibleMonths.map((month) => (
          <MonthView
            key={month.toISOString()}
            month={month}
            selectedStart={selectedRange.start}
            selectedEnd={selectedRange.end}
            onSelectDate={onDateSelect}
            showMonthHeader
          />
        ))}
      </div>
    </div>
  );
}

function PaginatedDatePicker({ selectedRange, visibleMonths, onDateSelect }: DatePickerProps) {
  const initialPageIndex = findInitialPageIndex(visibleMonths, selectedRange.start);
  const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex);

  const paginatedMonths = getOverlappingMonthPairs(visibleMonths);
  const currentPair = paginatedMonths[currentPageIndex] || [];
  const totalPages = paginatedMonths.length;

  const handleOnPreviousClick = () => {
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleOnNextClick = () => {
    setCurrentPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className="mx-auto">
      <div className="gap-x-xl border-border_disabled grid grid-cols-[1fr_auto_1fr] border-b">
        {currentPair.map((month, index) => {
          return (
            <Fragment key={month.toISOString()}>
              <div className="pb-md">
                <div className="flex items-center">
                  {index === 0 && (
                    <Fab
                      onClick={handleOnPreviousClick}
                      disabled={currentPageIndex === 0}
                      size="sm"
                      variant="secondary"
                      icon={<Icon variant="arrow-left" />}
                    />
                  )}
                  <h2 className="text-fg_primary mx-auto text-xl capitalize">
                    {moment(month).locale(getLang()).format('MMMM')}
                  </h2>
                  {index === 1 && (
                    <Fab
                      onClick={handleOnNextClick}
                      disabled={currentPageIndex === totalPages - 1}
                      size="sm"
                      variant="secondary"
                      icon={<Icon variant="arrow-right" />}
                    />
                  )}
                </div>
                <div className="grid h-[48px] grid-cols-8 place-content-center text-center text-xs">
                  {/* Empty cell for week number header */}
                  <div></div>
                  <WeekDays />
                </div>
                <MonthView
                  key={month.toISOString()}
                  month={month}
                  selectedStart={selectedRange.start}
                  selectedEnd={selectedRange.end}
                  onSelectDate={onDateSelect}
                />
              </div>
              {index === 0 && <div className="bg-border_disabled h-full w-[1px]"></div>}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
export default function DatePicker(props: DatePickerProps) {
  const SelectedView = View[props.type];
  return <SelectedView {...props} />;
}
